/* eslint-disable no-unused-expressions */
import React, { useCallback, useEffect, useState } from 'react';
import { shallowEqual, useDispatch, useSelector, connect } from 'react-redux';
import classNames from 'classnames/bind';
import { PromoBnpl, useCustomEvent } from '@beef/shop-common';
import { DevicePreview } from '@beef/smart-kit';

import { HEADER_HEIGHT_OFFSET } from 'constants/common';
import { COOKIES_NAMES } from 'constants/cookies';
import {
  BUTTON_TITLE,
  PROMO_CHEAPER_CAPTION,
  PROMO_SUBSCRIBE,
} from 'pages/ProductDetails/views/_ViewLayout/components/SmartDevicePreview/constants';
import pushEvent, {
  ADD_TO_COMPARISON_FROM_DETAILS_PAGE,
  ADD_TO_FAVORITE_FROM_DETAILS_PAGE,
  REMOVE_FROM_COMPARISON_FROM_DETAILS_PAGE,
  REMOVE_FROM_FAVORITE_FROM_DETAILS_PAGE,
} from 'pushEvent';
import { clickPlace } from 'pushEvent/constants';
import { deviceMapper } from 'services';
import { cookiesDataService } from 'services/cookiesDataService';
import { SOURCES_NAMES } from 'store/common/basket/constants';
import {
  getIsBundleInBasket,
  getIsItemInBasket,
  isBasketDataFetchedSelector,
} from 'store/common/basket/selectors';
import { toggleCompareProduct } from 'store/common/compare/actions';
import { getComparedProductIds } from 'store/common/compare/selectors';
import { isScreenBelow800 } from 'store/common/settings/breakpoint/selectors';
import {
  getIsFavoritesActive,
  getisCompareActive,
  selectIsCJBundle,
} from 'store/common/settings/selectors';
import { DELIVERY_TYPE_COURIER, DELIVERY_TYPE_TAKEAWAY } from 'store/constants';
import {
  addToBasket,
  onProductDetailsOpenBnplTooltip,
  onProductDetailsPromoLinkClick,
  onProductDetailsReviewsClick,
  setActiveSectionWithBnpl,
  setActiveSectionWithDelivery,
  setIsBundleSwitchedAction,
  setProductIsFavoriteAction,
} from 'store/ProductDetails/common/actions';
import { FOLLOW_TABS_SCROLL_ID } from 'store/ProductDetails/common/constants';
import { TabEnum } from 'store/ProductDetails/constants';
import {
  getSubscribeProductIdsFromStorage,
  onOpenOutOfStockForm,
  submitOutOfStockProductForm,
} from 'store/ProductDetails/outofstock/actions';
import { getOutOfStockDefaultMessage } from 'store/ProductDetails/outofstock/selectors';
import { getPreorderStartMessage } from 'store/ProductDetails/preorder/selectors';
import {
  capacitySelector,
  contentPromotionSelector,
  getAbsoluteBaseUrl,
  getBadges,
  getBrandName,
  getColors,
  getDeliveryData,
  getDeliveryTakeawayData,
  getFeedbackCount,
  getFeedbackRating,
  getFeedbackTabUrl,
  getGalleryImages,
  getPageTitle,
  getProductArticle,
  getProductId,
  getPromotionExtraProducts,
  getPromotionId,
  getTypes,
  isDetailsDeviceBestPricePromotionSelector,
  isProductFavoriteSelector,
  promoBundleSelector,
  promoCreditSelector,
  selectCalculatedOldPrice,
  selectCalculatedPrice,
  selectCalculatedPriceDiscount,
  selectIsBundleActive,
  selectPromoBnplList,
  selectPromoTradeIn,
  tShirtSelector,
} from 'store/ProductDetails/selectors';
import { setHashToUrl } from 'utils/common';
import { scrollById } from 'utils/scroll';
import K50BotInvisibleSeoTag from 'components/K50BotInvisibleSeoTag';
import { onLookBundle } from 'store/ProductDetails/bundles/actions';

import styles from './styles.pcss';

const cx = classNames.bind({ ...styles });

const isProductSubscribed = (id) => {
  const list = getSubscribeProductIdsFromStorage();
  return list.includes(id);
};

type SmartDevicePreviewProps = {
  detailsType: string;
  onLookBundleClick: () => void;
  onOpenCheaperModal: () => void;
  size: string;
};

export const SmartDevicePreview_ = ({
  detailsType,
  onLookBundleClick,
  onOpenCheaperModal,
  size,
}: SmartDevicePreviewProps) => {
  const dispatch = useDispatch();
  const { publishDOM } = useCustomEvent();

  const [isSubscribed, setIsSubscribed] = useState(false);
  const [isSubscribeFetching, setIsSubscribeFetching] = useState(false);

  const {
    absoluteBaseUrl,
    article,
    badges,
    bnplList,
    brandName,
    capacityType,
    colors,
    contentPromotion,
    deliveryData,
    deliveryTakeawayData,
    discount,
    extraProducts,
    feedbackUrl,
    idsInCmp,
    isBestPricePromotion,
    isBundleInBasket,
    isItemInBasket,
    isBaskedFetched,
    isBundleSwitched,
    isCJBundle,
    isCompareActive,
    isFavoritesActive,
    isMobile,
    isProductFavorite,
    oldPrice,
    outOfStockMessage,
    preorderStartMessage,
    price,
    productId,
    promoBundle,
    promoCredit,
    promoTradeIn,
    promotionId,
    rateCount,
    rateFill,
    slides,
    tShirtType,
    title,
    types,
  } = useSelector(
    (state) => ({
      absoluteBaseUrl: getAbsoluteBaseUrl(state),
      article: getProductArticle(state),
      badges: getBadges(state),
      bnplList: selectPromoBnplList(state),
      brandName: getBrandName(state),
      capacityType: capacitySelector(state),
      colors: getColors(state),
      contentPromotion: contentPromotionSelector(state),
      deliveryData: getDeliveryData(state),
      deliveryTakeawayData: getDeliveryTakeawayData(state),
      discount: selectCalculatedPriceDiscount(state),
      extraProducts: getPromotionExtraProducts(state),
      feedbackUrl: getFeedbackTabUrl(state),
      idsInCmp: getComparedProductIds(state),
      isBestPricePromotion: isDetailsDeviceBestPricePromotionSelector(state),
      isBundleSwitched: selectIsBundleActive(state),
      isBaskedFetched: isBasketDataFetchedSelector(state),
      isBundleInBasket: (id: number) => getIsBundleInBasket(state, id),
      isItemInBasket: (id: number) => getIsItemInBasket(state, id),
      isCJBundle: selectIsCJBundle(state),
      isCompareActive: getisCompareActive(state),
      isFavoritesActive: getIsFavoritesActive(state),
      isMobile: isScreenBelow800(state),
      isProductFavorite: isProductFavoriteSelector(state),
      oldPrice: selectCalculatedOldPrice(state),
      outOfStockMessage: getOutOfStockDefaultMessage(state),
      preorderStartMessage: getPreorderStartMessage(state),
      price: selectCalculatedPrice(state),
      productId: getProductId(state),
      promoBundle: promoBundleSelector(state),
      promoCredit: promoCreditSelector(state),
      promoTradeIn: selectPromoTradeIn(state),
      promotionId: getPromotionId(state),
      rateCount: getFeedbackCount(state),
      rateFill: getFeedbackRating(state),
      slides: getGalleryImages(state),
      tShirtType: tShirtSelector(state),
      title: getPageTitle(state),
      types: getTypes(state),
    }),
    shallowEqual,
  );

  useEffect(() => {
    if (isBaskedFetched && isItemInBasket(productId) && !isBundleInBasket(promotionId)) {
      dispatch(setIsBundleSwitchedAction(false));
    }
  }, [isBaskedFetched, promotionId, productId]);

  const isInBasket = useSelector((state) => getIsItemInBasket(state, productId?.toString()));

  const onPurchase = useCallback(() => {
    dispatch(
      addToBasket({
        origin: 'card',
        promotionId: isBundleSwitched ? promotionId : null,
        source: SOURCES_NAMES.PRODUCT_DETAILS_PAGE,
      }),
    );
  }, [dispatch, isBundleSwitched]);

  const switchBundle = (isActive) => {
    dispatch(setIsBundleSwitchedAction(isActive));
  };

  const onSetIsSubscribed = useCallback(() => {
    setIsSubscribed(isProductSubscribed(productId));
  }, [productId, setIsSubscribed, isProductSubscribed]);

  const onSuccessSubmitOutOfStock = () => {
    setIsSubscribed(isProductSubscribed(productId));
    setIsSubscribeFetching(false);
  };

  const onSubscribe = useCallback(
    (email, name) => {
      setIsSubscribeFetching(true);
      dispatch(submitOutOfStockProductForm({ callback: onSuccessSubmitOutOfStock, email, name }));
    },
    [dispatch, productId, setIsSubscribed, isProductSubscribed, setIsSubscribeFetching],
  );

  const onSetSlug = useCallback((slug) => {
    window.location.href = slug;
  }, []);

  const onOpenSubscribeForm = useCallback(() => {
    setIsSubscribed(false);
  }, []);

  useEffect(() => {
    if (detailsType === 'subscribePreview' && !isSubscribed) {
      // YM
      dispatch(onOpenOutOfStockForm(clickPlace.PDP, productId));
    }
  }, [detailsType, isSubscribed]);

  const onOpenCheaperForm = useCallback(() => {
    onOpenCheaperModal();
    dispatch(onProductDetailsPromoLinkClick(PROMO_CHEAPER_CAPTION));
  }, [dispatch]);

  const onReviewsLinkClick = useCallback(() => {
    // YM
    dispatch(onProductDetailsReviewsClick(clickPlace.PDP));
  }, [dispatch]);

  const onSetCurrentTab =
    (section, linkTitle = '') =>
    (event) => {
      event.preventDefault();
      dispatch(setActiveSectionWithDelivery(section));
      setHashToUrl(section);
      scrollById(FOLLOW_TABS_SCROLL_ID, HEADER_HEIGHT_OFFSET);
      // YM
      dispatch(onProductDetailsPromoLinkClick(clickPlace.PDP, linkTitle));
    };

  useEffect(() => {
    onSetIsSubscribed();
  }, [productId, setIsSubscribed, isProductSubscribed]);

  useEffect(() => {
    const isFavoriteSelected = cookiesDataService.getIsIncludesData(
      COOKIES_NAMES.FAVORITE_DEVICES,
      productId,
    );
    dispatch(setProductIsFavoriteAction(isFavoriteSelected));
  }, []);

  let delivery;
  let takeaway;

  if (deliveryData?.courier?.url && deliveryData?.courier?.title) {
    delivery = {
      label: deliveryData.courier.title,
      link: deliveryData.courier.url,
      onClick: onSetCurrentTab(
        `${TabEnum.CONTENT_TAB_DELIVERY}/${DELIVERY_TYPE_COURIER}`,
        deliveryData.courier.title,
      ),
    };
  }

  if (deliveryTakeawayData?.url && deliveryTakeawayData?.title) {
    takeaway = {
      label: deliveryTakeawayData.title,
      link: deliveryTakeawayData.url,
      onClick: onSetCurrentTab(
        `${TabEnum.CONTENT_TAB_DELIVERY}/${DELIVERY_TYPE_TAKEAWAY}`,
        deliveryTakeawayData.title,
      ),
    };
  }

  let promoStartSales;

  if (detailsType === 'preOrder') {
    promoStartSales = preorderStartMessage;
  }

  const [subscribeData, setSubscribeData] = useState({
    isLoading: false,
    isSubscribed: false,
    onClick: onSubscribe,
    size: 'l',
    title: 'Отправить',
    variant: 'main',
  });

  useEffect(() => {
    if (detailsType === 'subscribePreview') {
      const data = {
        isLoading: isSubscribeFetching,
        isSubscribed,
        onClick: isSubscribed ? onOpenSubscribeForm : onSubscribe,
        size: 'l',
        title: isSubscribed ? 'Отправить другую заявку' : 'Отправить',
        variant: isSubscribed ? 'tertiary' : 'main',
      };

      setSubscribeData(data);
    }
  }, [isSubscribeFetching, isSubscribed, detailsType]);

  let subscribePreviewData;

  if (detailsType === 'subscribePreview') {
    subscribePreviewData = {
      personalDataLink: '//static.beeline.ru/upload/images/soglasia_27122017.pdf',
      promoSaleSoon: outOfStockMessage,
      promoSubscribe: isSubscribed ? PROMO_SUBSCRIBE.SUCCESS : PROMO_SUBSCRIBE.INITIAL,
    };
  }

  const isPreOrder = detailsType === 'preOrder';
  const promoBadges = deviceMapper.getDeviceMappedBadges(badges);
  const imgOptions = deviceMapper.getDeviceMappedClides(slides);
  const deviceColors = deviceMapper.getDeviceMappedColors(colors);
  const capacity = deviceMapper.getDeviceMappedCapacity(capacityType);
  const tShirtSize =
    deviceMapper.getDeviceMappedCapacity(tShirtType)?.length > 0 ?
      deviceMapper.getDeviceMappedCapacity(tShirtType)
    : null;

  function isCompared(deviceId) {
    return idsInCmp.filter((id) => id === deviceId).length > 0;
  }

  const handleCmpClick = useCallback(() => {
    dispatch(toggleCompareProduct({ productId }));
    const pushName =
      isCompared(productId) ?
        REMOVE_FROM_COMPARISON_FROM_DETAILS_PAGE
      : ADD_TO_COMPARISON_FROM_DETAILS_PAGE;

    pushEvent(pushName, {
      brand: brandName,
      id: article,
      name: title,
      place: 'pdp',
      position: null,
    });
  }, [dispatch, toggleCompareProduct, isCompared, productId, title, brandName]);

  const handleOpenTooltip = () => {
    dispatch(onProductDetailsOpenBnplTooltip());
  };

  let deviceData = {
    absoluteBaseUrl,
    capacity,
    cmpButtonText: isCompared(productId) ? 'Удалить из сравнения' : 'Добавить в сравнение',
    color: deviceColors,
    discount,
    imgOptions,
    isInCmp: isCompared(productId),
    isNewBundle: isCJBundle,
    isProductDetails: true,
    name: title,
    oldPrice,
    onCmpClick: isCompareActive ? () => handleCmpClick() : null,
    price,
    promo: bnplList.map((installment) => (
      <PromoBnpl
        onOpen={handleOpenTooltip}
        {...installment}
        isMobile={isMobile}
        tooltipButtonOnClick={() => {
          dispatch(setActiveSectionWithBnpl(installment.tooltipButtonHref));
        }}
      />
    )),
    promoBadges,
    promoBundle: {
      ...promoBundle,
      onClick: onLookBundleClick,
      extraProducts,
      isActive: isBundleSwitched,
      onClick: onSetCurrentTab(TabEnum.CONTENT_TAB_PROMO, promoBundle?.label),
      onSwitch: () => switchBundle(!isBundleSwitched),
    },
    promoCheaper: isBestPricePromotion && {
      caption: PROMO_CHEAPER_CAPTION,
      onClick: onOpenCheaperForm,
    },
    promoCode: {
      ...contentPromotion,
      onClick: onSetCurrentTab(TabEnum.CONTENT_TAB_PROMO, contentPromotion?.label),
      promoStartSales,
    },
    promoCredit: {
      ...promoCredit,
      onClick: onSetCurrentTab(TabEnum.CONTENT_TAB_PAYMENT, promoCredit?.label),
    },
    promoStartSales,
    promoTradeIn: {
      ...promoTradeIn,
    },
    reviews:
      rateFill > 0 && rateCount > 0 ?
        {
          average: rateFill,
          count: rateCount,
          link: feedbackUrl,
          onClick: onReviewsLinkClick,
        }
      : undefined,
    tShirtSize,
    types,
    ...subscribePreviewData,
  };

  if (!isPreOrder) {
    deviceData = {
      ...deviceData,
      delivery,
      pickup: takeaway,
    };
  }

  const redirectToBasket = () => {
    window.location.href = '/basket/';
  };

  const purchaseTitle =
    detailsType === 'preOrder' ? BUTTON_TITLE.PRE_ORDER : BUTTON_TITLE.TO_BASKET;
  const purchaseData = {
    onClick: isInBasket ? redirectToBasket : onPurchase,
    size: 'l',
    title: isInBasket ? BUTTON_TITLE.IN_BASKET : purchaseTitle,
    variant: isInBasket ? 'secondary' : 'main',
  };

  const setIsFavoriteDevice = useCallback(() => {
    const favoriteDeviceList = cookiesDataService.setActualData(
      COOKIES_NAMES.FAVORITE_DEVICES,
      productId,
    );
    publishDOM(COOKIES_NAMES.FAVORITE_DEVICES, favoriteDeviceList);

    const pushName =
      isProductFavorite ?
        REMOVE_FROM_FAVORITE_FROM_DETAILS_PAGE
      : ADD_TO_FAVORITE_FROM_DETAILS_PAGE;

    pushEvent(pushName, {
      brand: brandName,
      id: article,
      name: title,
      place: 'pdp',
      position: null,
    });
  }, [dispatch, isProductFavorite]);

  return (
    <div className={cx('devicePreviewWrapepr')}>
      <DevicePreview
        data={deviceData}
        directionSlider={size === 'm' ? 'horizontal' : 'vertical'}
        isFavoriteSelected={isProductFavorite}
        onChangeColor={onSetSlug}
        onChangeType={onSetSlug}
        purchaseData={purchaseData}
        setFavorite={isFavoritesActive && setIsFavoriteDevice}
        size={size}
        subscribeData={subscribeData}
        type={detailsType}
      />
      <K50BotInvisibleSeoTag isOutOfStock={detailsType === 'subscribePreview'} />
    </div>
  );
};

const mapDispatchToProps = {
  onLookBundleClick: onLookBundle,
};

export const SmartDevicePreview = connect(null, mapDispatchToProps)(SmartDevicePreview_);
